// =============================================
// LLM Provider Factory
// v3.0: Dual LLM Engine with per-stage configuration
// =============================================

import type {
  LLMProvider,
  LLMProviderType,
  LLMOptions,
  LLMCallOptions,
  LLMResponse,
  ContentEngineStage,
  StageModelConfig,
} from "./types";
import { DEFAULT_STAGE_MODELS } from "./types";
import { claudeProvider } from "./claudeProvider";
import { openaiProvider } from "./openaiProvider";
import { logDebug, logError } from "../contentEngine/contentLogger";

const providers: Record<LLMProviderType, LLMProvider> = {
  claude: claudeProvider,
  openai: openaiProvider,
};

/**
 * Get an LLM provider by type.
 */
export function getLLMProvider(type: LLMProviderType): LLMProvider {
  const provider = providers[type];
  if (!provider) {
    throw new Error(`Unknown LLM provider: ${type}`);
  }
  return provider;
}

/**
 * Get the configured provider + model for a specific Content Engine stage.
 * Uses DEFAULT_STAGE_MODELS unless overridden.
 */
export function getStageConfig(
  stage: ContentEngineStage,
  overrides?: Partial<Record<ContentEngineStage, StageModelConfig>>,
): StageModelConfig {
  return overrides?.[stage] || DEFAULT_STAGE_MODELS[stage];
}

/**
 * Call the LLM for a specific Content Engine stage.
 * Automatically selects provider and model based on stage configuration.
 */
export async function callLLMForStage(
  stage: ContentEngineStage,
  prompt: string,
  options?: Omit<LLMCallOptions, "model" | "step"> & { step?: string },
  stageOverrides?: Partial<Record<ContentEngineStage, StageModelConfig>>,
): Promise<LLMResponse> {
  const config = getStageConfig(stage, stageOverrides);
  const provider = getLLMProvider(config.provider);
  return provider.call(prompt, {
    ...options,
    model: config.model,
    // Auto-inject step from stage param (caller's options.step wins if provided)
    step: options?.step ?? stage,
  });
}

/**
 * Parse JSON from LLM response text.
 * Handles markdown code blocks (```json ... ```)
 */
export function parseLLMJSON<T>(text: string): T {
  let cleaned = text.trim();

  // Try 1: Extract from markdown code block
  const jsonBlockMatch = cleaned.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  if (jsonBlockMatch) {
    cleaned = jsonBlockMatch[1].trim();
  }

  // Try 2: If still not valid JSON, find first { and last }
  if (!cleaned.startsWith("{") && !cleaned.startsWith("[")) {
    const firstBrace = cleaned.indexOf("{");
    const firstBracket = cleaned.indexOf("[");
    const start = firstBrace >= 0 && firstBracket >= 0
      ? Math.min(firstBrace, firstBracket)
      : Math.max(firstBrace, firstBracket);

    if (start >= 0) {
      const isArray = cleaned[start] === "[";
      const lastChar = isArray ? cleaned.lastIndexOf("]") : cleaned.lastIndexOf("}");
      if (lastChar > start) {
        logDebug("LLM", `parseLLMJSON: extracting JSON from position ${start}-${lastChar} (text started with: "${cleaned.slice(0, 30)}...")`);
        cleaned = cleaned.slice(start, lastChar + 1);
      }
    }
  }

  try {
    return JSON.parse(cleaned);
  } catch (err) {
    logError("LLM", `parseLLMJSON: failed to parse. First 300 chars: ${cleaned.slice(0, 300)}`);
    throw new Error(`LLM zwrócił niepoprawny JSON. Początek: "${cleaned.slice(0, 80)}..."`);
  }
}

// Re-export types
export type {
  LLMProvider,
  LLMProviderType,
  LLMOptions,
  LLMCallOptions,
  LLMResponse,
  ContentEngineStage,
  StageModelConfig,
};
export { DEFAULT_STAGE_MODELS };
